import { useRouteParams } from "@vueuse/router";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import {
  useCurrentExperienceAdditionalCitiesQuery,
  EXPERIENCE_ADDITIONAL_CITIES_QUERY_KEY,
} from "@/features/experience-shared/queries/useExperienceAdditionalCitiesQuery";
import { buildAdditionalCitiesPayload } from "@/features/experience-shared/utils/address-utils";
import { useCurationAdditionalCities } from "@/features/experience-curation/sections/location/additional-cities/useCurationAdditionalCities";
import type { ExperienceAdditionalCities } from "@/types/generated/MetadataExperiencesApi";

export function useSaveAdditionalCitiesMutation() {
  const experienceId = useRouteParams<string>("id");
  const queryClient = useQueryClient();
  const metadataService = useMetadataExperienceApi();
  const additionalCitiesQuery = useCurrentExperienceAdditionalCitiesQuery();
  const additionalCitiesStore = useCurationAdditionalCities();

  return useMutation({
    mutationFn: async () => {
      if (!additionalCitiesStore.hasChanges) return;

      const id = experienceId.value;
      const existingId = additionalCitiesQuery.data.value?.id;
      const cityCodes = buildAdditionalCitiesPayload(additionalCitiesStore.model);

      if (cityCodes.length === 0 && existingId) {
        await metadataService.deleteAdditionalCities(existingId);
        return;
      }

      if (cityCodes.length === 0) return;

      const payload: ExperienceAdditionalCities = {
        experience_id: id,
        additional_cities: cityCodes,
      };

      if (existingId) {
        await metadataService.updateAdditionalCities(id, existingId, payload);
      } else {
        await metadataService.createAdditionalCities(id, payload);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPERIENCE_ADDITIONAL_CITIES_QUERY_KEY] }),
  });
}
